import React from 'react'
import _ from 'lodash'
import moment from 'moment'
import { NotificationManager } from 'react-notifications'
import queryString from 'query-string'
import confirm from './confirm'
import ReactGA from 'react-ga4'

/*
 * message: string or functional component
 * options: object with props to pass to the popup component (e.g. showCloseButton)
 */
export const onConfirm = (message, callback, options) => {
  confirm(message, callback, options)
}

export const showNotification = (message, duration = 5000) => {
  NotificationManager.success(message, '', duration)
}

export const showErrorNotification = (message, duration = 10000) => {
  NotificationManager.error(message, '', duration)
}

const _formatDate = (date, timeZone, format) => {
  const momentLocale = window.I18n.locale == 'jp' ? 'ja' : window.I18n.locale
  if (date) {
    let formattedDate = moment(date)
    if (timeZone) {
      formattedDate = formattedDate.tz(timeZone)
    }
    return formattedDate.locale(momentLocale).format(format)
  } else {
    return ''
  }
}

export const formatDate = (date, timeZone, format = 'MMM D, YYYY') => (
  _formatDate(date, timeZone, format)
)

// AM/PM format default
export const formatDateTime = (date, timeZone, format = 'MMM DD, YYYY h:mm A') => (
  _formatDate(date, timeZone, format)
)

export const getTodayWithTimezone = (timeZone) => {
  return moment().tz(timeZone)
}

let uniqueId = 1
export const getUniqueElementId = () => {
  const currentUniqueId = uniqueId
  uniqueId++
  return `element-id-${ currentUniqueId }`
}

/*
 * items: array or map of {
 *   id: ...
 *   [labelField]: ...
 * }
 */
export const mapToSelectArray = (items, { labelField = 'name', changeNone = true } = {}) => (
  _.values(items).map(item => ({
    value: ((item.id === 'none' && changeNone) ? '' : item.id),
    label: item[labelField],
  }))
)

/*
 * This method compares the content of 2 children arrays by doing a shallow 
 * comparison between corresponding children.
 * Useful in shouldComponentUpdate methods.
 */
export const areChildrenEqual = (children1, children2) => {
  const children1Count = React.Children.count(children1)
  const children2Count = React.Children.count(children2)
  if (children1Count !== children2Count) {
    return false
  }

  const childrenArray1 = React.Children.toArray(children1)
  const childrenArray2 = React.Children.toArray(children2)
  for (let i = 0; i < children1Count; i++) {
    if (childrenArray1[i] !== childrenArray2[i]) {
      return false
    }
  }

  return true
}

export const getAuthenticityToken = () => (
  $('head meta[name=\'csrf-token\']').attr('content')
)

export const callAPI = (url, method = 'GET', data = {}, headers = {}) => {
  let fullURL = url
  if (method === 'GET') {
    fullURL = `${ url }${ url.includes('?') ? '&' : '?' }${ $.param(data) }`
  }

  let fetchData = {
    method,
    credentials: 'same-origin',
  }
  if (method === 'POST' || method === 'PATCH') {
    fetchData = {
      ...fetchData,
      headers: {
        'X-CSRF-Token': getAuthenticityToken(),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: queryString.stringify(data),
    }
  } else if (method === 'PUT') {
    fetchData = {
      ...fetchData,
      headers: {
        'X-CSRF-Token': getAuthenticityToken(),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: queryString.stringify(data),
    }
  } else if (headers !== {}) {
    fetchData = {
      ...fetchData,
      headers: headers,
    }
  }

  return new Promise((resolve, reject) => {
    fetch(fullURL, fetchData)
    .then(response => {
      // PATCH
      if (response.status === 204) {
        return Promise.all([ response, {} ])
      } else {
        return Promise.all([ response, response.json() ])
      }
    })
    .then(([ response, json ]) => {
      if ((response.status === 200 || response.status === 204) && _.isEmpty(json.errorMessage)) {
        resolve(json)
      } else {
        reject(json.errorMessage, json)
      }
    })
    .catch(() => {
      reject()
    })
  })
}

// Recursively transforms an object's keys to snake case
export const objectToSnakeCase = object => {
  if (_.isUndefined(object) ||
      _.isNull(object) ||
      typeof(object) === 'number' ||
      typeof(object) === 'string') {
    return object
  }

  if (_.isArray(object)) {
    return object.map(objectToSnakeCase)
  }

  object = _.mapKeys(object, (v, key) => _.snakeCase(key))
  object = _.mapValues(object, objectToSnakeCase)

  return object
}

export const initializeReactGA4 = () => {
  ReactGA.initialize([
    {
      trackingId: 'G-F4RE5398BV',
      gaOptions: {
        titleCase: false,
        name: 'ga4',
      },
    },
  ])
}

export const switchExpression = (lookupTable) => {
  let defaultValue = null
  const processedLookupTable = Object.keys(lookupTable).reduce((newTable, oldKey) => {
    if (oldKey === '*') {
      defaultValue = lookupTable[oldKey]
    } else {
      oldKey.split('|').forEach(key => newTable[key] = lookupTable[oldKey])
    }
    return newTable
  }, {})
  const search = (key) => processedLookupTable[key] || defaultValue

  return search
}

export const modifyUrl = (prefix) => {
  const url = new URL(origin)

  const { protocol, host, pathname, search, hash } = url

  if (host.startsWith('www.')) {
    const newHost = host.replace('www.', 'share-cdn.')
    return `${protocol}//${newHost}${pathname}${search}${hash}`
  } else {
    return `${protocol}//${prefix}.${host}${pathname}${search}${hash}`
  }
}

export const handleRowClick = (event, shouldHideRegistration, resultsPath) => {
  if (shouldHideRegistration) {
    event.preventDefault()
  } else {
    return
  }

  window.top.location.href = modifyUrl('share-cdn') + resultsPath.slice(1)
}

export const customBaseQuery = async (args, api) => {
  const { urlKey = '', method = 'GET', params = {}, url = '' } = args;
  
  const state = api.getState();
  
  const fullUrl = url.length > 0 ? url : state.misc.data?.routes?.[urlKey]

  if (!fullUrl) {
    return { 
      error: { status: 404, data: { message: `URL not found in miscData for key: ${urlKey}` } } 
    };
  }
  const data = await callAPI(fullUrl, method, params);

  return { data };
};